/*
 * Texte der Startseiten nach dem Login: /user/home (Schützen) und /club/home (Vereine).
 *
 * Wie publicPages.js: CommonJS, nur Daten, keine Imports – wird auch von
 * scripts/export-texts.js gelesen (npm run texte).
 * Sätze mit Zahlen oder Namen (z. B. „Dein letztes Training war am …“) sind Funktionen,
 * weil die Werte erst in der App feststehen.
 * Welche Kachel jemand sieht, entscheidet die View (Freischaltungen des Vereins),
 * hier stehen nur die Texte.
 */

const user = {
  kickerFallback: 'Dein Schießbuch',
  greeting: firstName => (firstName ? `Hallo ${firstName}` : 'Hallo'),

  lead: {
    running: (time, club) =>
      `Du bist seit ${time} Uhr${club ? ` bei ${club}` : ''} eingebucht. Wenn du fertig bist, beendest du den Eintrag unter „Training eintragen“.`,
    lastEntry: (date, weapon) =>
      `Dein letztes Training war am ${date}${weapon ? ` mit der ${weapon}` : ''}. Das nächste trägst du am besten gleich am Stand ein.`,
    empty:
      'Dein Schießbuch ist noch leer. Das erste Training trägst du am besten direkt am Stand ein, am Handy geht das genauso wie am PC.'
  },

  actions: {
    primary: { text: 'Training eintragen', to: '/user/recordtime' },
    secondary: { text: 'Schießbuch öffnen', to: '/user/shootingbook' }
  },

  stats: {
    entries: { label: 'Einträge im Schießbuch' },
    open: { label: 'Offene Einträge', caption: 'noch nicht abgeschlossen' },
    weapons: {
      label: 'Waffen',
      captionReceived: n => (n === 1 ? '1 überlassen bekommen' : `${n} überlassen bekommen`),
      captionLent: n => (n === 1 ? '1 an andere überlassen' : `${n} an andere überlassen`)
    },
    education: { label: 'Ausbildungen', caption: 'zum Beispiel Standaufsicht' }
  },

  setup: {
    title: 'So richtest du dein Schießbuch ein',
    text: 'Ein paar Angaben brauchen wir noch, dann kann es losgehen. Was schon erledigt ist, ist abgehakt.',
    steps: [
      {
        id: 'club',
        title: 'Verein zuordnen',
        text: 'Unter „Vereine“ suchst du deinen Verein und fügst ihn hinzu. Der Verein bestätigt danach deine Mitgliedschaft.',
        link: { text: 'Zu den Vereinen', to: '/user/myclub' }
      },
      {
        id: 'mainClub',
        title: 'Hauptverein festlegen',
        text: 'Sobald dein Verein dich bestätigt hat, wählst du ihn im Profil als Hauptverein aus.',
        link: { text: 'Zum Profil', to: '/user/profil' }
      },
      {
        id: 'weapons',
        title: 'Waffen anlegen',
        text: 'Trag deine Waffen ein und hinterlege, welche Disziplinen du damit schießt. Die Auswahl beim Eintragen wird dadurch kürzer.',
        link: { text: 'Zu den Waffen', to: '/user/weapon' }
      },
      {
        id: 'education',
        optional: true,
        title: 'Ausbildungen eintragen',
        text: 'Hast du zum Beispiel die Ausbildung zur Standaufsicht, trag sie hier ein. Dann kann dein Verein dich dafür freischalten.',
        link: { text: 'Zu den Ausbildungen', to: '/user/education' }
      }
    ]
  },

  tilesTitle: 'Alles auf einen Blick',
  tiles: [
    {
      id: 'shootingbook',
      icon: 'mdi-book-open-variant',
      title: 'Schießbuch',
      text: 'Alle Trainings in einer Liste. Hier reichst du Einträge zur Bestätigung ein und exportierst sie als PDF oder Excel.',
      to: '/user/shootingbook'
    },
    {
      id: 'recordtime',
      icon: 'mdi-plus-circle-outline',
      title: 'Training eintragen',
      text: 'Im eigenen Verein ein- und ausbuchen oder ein Training bei einem anderen Verein oder Wettkampf nachtragen.',
      to: '/user/recordtime'
    },
    {
      id: 'weapons',
      icon: 'mdi-pistol',
      title: 'Waffen',
      text: 'Deine Waffen mit WBK-Nummer, NWR-IDs und Bedürfnis. Dazu der Bedürfnis-Check nach § 14 WaffG.',
      to: '/user/weapon'
    },
    {
      id: 'analytics',
      icon: 'mdi-chart-line',
      title: 'Auswertung',
      text: 'Wie oft und womit du trainiert hast, für jeden Zeitraum, den du brauchst.',
      to: '/user/analytics'
    },
    {
      id: 'weaponpool',
      icon: 'mdi-archive-outline',
      title: 'Waffenpool',
      text: 'Waffen aus dem Verein, die du im Training nutzen darfst. Eigene Waffen kannst du hier auch zur Verfügung stellen.',
      to: '/user/weaponpool'
    },
    {
      id: 'workinghours',
      icon: 'mdi-hammer-wrench',
      title: 'Arbeitsstunden',
      text: 'Stunden eintragen und sehen, was der Verein schon freigegeben hat.',
      to: '/user/arbeitsstunden'
    },
    {
      id: 'assignments',
      icon: 'mdi-calendar-account',
      title: 'Arbeitseinsätze',
      text: 'Die geplanten Einsätze in deinem Verein. Hier sagst du zu oder ab.',
      to: '/user/arbeitseinsaetze'
    },
    {
      id: 'rangeofficer',
      icon: 'mdi-account-tie',
      title: 'Schießleiter heute',
      text: 'Wer gerade am Stand eingebucht ist, wenn du als Schießleiter Dienst hast.',
      to: '/user/schiessleiter/today'
    },
    {
      id: 'education',
      icon: 'mdi-school-outline',
      title: 'Ausbildungen',
      text: 'Deine Qualifikationen, zum Beispiel als Standaufsicht.',
      to: '/user/education'
    },
    {
      id: 'clubs',
      icon: 'mdi-home-group',
      title: 'Vereine',
      text: 'Die Vereine, in denen du Mitglied bist, und ob sie dich schon bestätigt haben.',
      to: '/user/myclub'
    }
  ],

  footnote: {
    text: 'Etwas fehlt oder funktioniert nicht so, wie du es erwartest?',
    link: { text: 'Schreib uns über das Feedback-Formular.', to: '/contactus' }
  }
};

const club = {
  kicker: 'Vereinsbereich',

  lead: {
    approvals: n =>
      n === 1
        ? 'Ein Schießbuch-Eintrag wartet auf eure Bestätigung.'
        : `${n} Schießbuch-Einträge warten auf eure Bestätigung.`,
    noApprovals: 'Im Moment wartet kein Schießbuch-Eintrag auf eine Bestätigung.',
    today: n =>
      n === 0
        ? 'Am Stand ist gerade niemand eingebucht.'
        : n === 1
          ? 'Gerade ist ein Schütze am Stand eingebucht.'
          : `Gerade sind ${n} Schützen am Stand eingebucht.`
  },

  actions: {
    primary: { text: 'Genehmigungen öffnen', to: '/club/authorization' },
    secondary: { text: 'Wer ist heute da?', to: '/club/today' }
  },

  stats: {
    approvals: { label: 'Offene Genehmigungen', caption: 'eingereichte Trainings' },
    today: { label: 'Heute am Stand', caption: 'eingebuchte Schützen' },
    rangeSafety: { label: 'Standaufsicht', caption: 'gerade eingebucht', captionNone: 'niemand eingebucht' },
    workinghours: { label: 'Arbeitsstunden', caption: 'warten auf Freigabe' }
  },

  setup: {
    title: 'So richtest du den Vereinsbereich ein',
    text: 'Bevor deine Mitglieder im Verein Trainings eintragen können, braucht es ein paar Grundlagen.',
    steps: [
      {
        id: 'ranges',
        title: 'Stände anlegen',
        text: 'Leg eure Stände und Bahnen an. Beim Eintragen wählen die Mitglieder den Stand aus, auf dem sie schießen.',
        link: { text: 'Zu den Ständen', to: '/club/ranges' }
      },
      {
        id: 'verbaende',
        title: 'Verbände auswählen',
        text: 'Wähle die Verbände aus, denen dein Verein angehört.',
        link: { text: 'Zu den Verbänden', to: '/club/verbaende' }
      },
      {
        id: 'members',
        title: 'Mitglieder bestätigen',
        text: 'Wer sich deinem Verein zugeordnet hat, erscheint unter „Mitglieder“. Dort bestätigst du die Mitgliedschaft und legst fest, wer Standaufsicht oder Schießleiter ist.',
        link: { text: 'Zu den Mitgliedern', to: '/club/members' }
      }
    ]
  },

  tilesTitle: 'Alles auf einen Blick',
  tiles: [
    {
      id: 'authorization',
      icon: 'mdi-check-decagram-outline',
      title: 'Genehmigungen',
      text: 'Eingereichte Trainings prüfen und bestätigen. Stimmt etwas nicht, lehnst du mit einer kurzen Begründung ab.',
      to: '/club/authorization'
    },
    {
      id: 'today',
      icon: 'mdi-account-group',
      title: 'Heute',
      text: 'Schießleiter, Standaufsicht und alle, die gerade schießen. Lässt sich auch als Monitor im Vereinsheim zeigen.',
      to: '/club/today'
    },
    {
      id: 'shootingbook',
      icon: 'mdi-book-open-variant',
      title: 'Schießbuch',
      text: 'Alle Einträge im Verein, mit Filtern und Export als PDF oder Excel.',
      to: '/club/shootingbook'
    },
    {
      id: 'members',
      icon: 'mdi-card-account-details-outline',
      title: 'Mitglieder',
      text: 'Mitgliedschaften bestätigen, RFID-Karten zuweisen und festlegen, wer Standaufsicht, Schießleiter oder Admin ist.',
      to: '/club/members'
    },
    {
      id: 'workinghours',
      icon: 'mdi-hammer-wrench',
      title: 'Arbeitsstunden',
      text: 'Eingereichte Stunden der Mitglieder prüfen und freigeben.',
      to: '/club/arbeitsstunden/freigaben'
    },
    {
      id: 'assignments',
      icon: 'mdi-calendar-account',
      title: 'Arbeitseinsätze',
      text: 'Einsätze planen, Mitglieder per E-Mail einladen und sehen, wer zugesagt hat.',
      to: '/club/arbeitsstunden/arbeitseinsaetze'
    },
    {
      id: 'weapons',
      icon: 'mdi-pistol',
      title: 'Vereinswaffen',
      text: 'Die Waffen des Vereins mit allen Angaben.',
      to: '/club/weapon'
    },
    {
      id: 'weaponpool',
      icon: 'mdi-archive-outline',
      title: 'Waffenpool',
      text: 'Festlegen, welche Waffen Mitglieder im Training nutzen dürfen, und sehen, wie oft sie im Einsatz sind.',
      to: '/club/weaponpool'
    },
    {
      id: 'ranges',
      icon: 'mdi-target',
      title: 'Stände',
      text: 'Die Stände und Bahnen, auf denen im Verein geschossen wird.',
      to: '/club/ranges'
    },
    {
      id: 'terminal',
      icon: 'mdi-nfc-variant',
      title: 'Terminal',
      text: 'Terminals am Schießstand verwalten und die Buchungen mit RFID-Karte nachvollziehen.',
      to: '/club/terminal'
    },
    {
      id: 'verbaende',
      icon: 'mdi-flag-outline',
      title: 'Verbände',
      text: 'Die Verbände, denen dein Verein angehört.',
      to: '/club/verbaende'
    },
    {
      id: 'profile',
      icon: 'mdi-cog-outline',
      title: 'Vereinsprofil',
      text: 'Anschrift und Kontaktdaten des Vereins. Hier findest du auch den Link für den Monitor im Vereinsheim.',
      to: '/club/profil'
    }
  ],

  footnote: {
    text: 'Etwas fehlt oder funktioniert nicht so, wie du es erwartest?',
    link: { text: 'Schreib uns über das Feedback-Formular.', to: '/contactus' }
  }
};

module.exports = { user, club };
